import {type List, type Map} from 'immutable';
import moment, {type Moment} from 'moment-timezone';

import {getInvoiceRangeFromFormData} from 'web-app/react/components/form-elements/invoice-period-picker/helpers';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import * as DatePeriodPickerConstants from 'web-app/react/components/form-elements/invoice-period-picker/constants';

import type * as Types from './types';
import * as Constants from './constants';

export const fetchPeriodFromSelectedPeriod = (
    invoicePeriod: {from?: string; to?: string; due?: string},
    format = 'YYYY-MM-DD',
) => {
    const from = moment(invoicePeriod.from, format).startOf('month').subtract(3, 'months').format('YYYY-MM-DD');
    const to = moment(invoicePeriod.to, format).format('YYYY-MM-DD');
    return {
        from,
        to,
        due: moment(invoicePeriod.due, format).format('YYYY-MM-DD'),
    };
};

export const convertInvoicePeriodData = (data: any) => {
    const {from, to} = getInvoiceRangeFromFormData(data);
    const {notify, due, invoiceDate} = data;

    // Checks if the Due Date is earlier than the Invoice Date (which defaults to today).
    // If so, overrides Invoice Date with the Due Date.
    if (!hasValue(invoiceDate) && moment(due, 'YYYY-MM-DD').isBefore(moment(), 'day')) {
        return {
            from,
            to,
            notify,
            due,
            invoiceDate: due,
        };
    }

    return {
        from,
        to,
        notify,
        due,
        invoiceDate,
    };
};

/**
 * A helper function that checks whether to show a "warning helper dot" next to an item
 * in the batch invoicing table.
 * A warning should be shown if the bill payer has been invoiced in a previous month and
 * the invoice for the current month differs from the most recent invoiced month.
 */
export const shouldShowWarning = (
    previewInvoicesMap: Map<string, Types.PreviewInvoice>,
    debtDetailsDataEntry: Types.DebtDetailsDataEntry,
    months: List<string>,
) => {
    const invoiceData = previewInvoicesMap.get(debtDetailsDataEntry.billPayer?.billPayerId);

    const previewInvoices = invoiceData?.invoices ?? [];

    const previewInvoiceSum = previewInvoices.reduce((acc, invoice) => acc + Number(invoice.amount), 0);

    const dataDetailsInvoiced = debtDetailsDataEntry.details.invoiced;

    const closestPreviousMonthWithInvoicedData = months
        .reverse()
        .find(month => dataDetailsInvoiced && Boolean(dataDetailsInvoiced.get(month)));

    const mostRecentInvoiceEntry = hasValue(closestPreviousMonthWithInvoicedData)
        ? dataDetailsInvoiced.get(closestPreviousMonthWithInvoicedData)
        : undefined;

    const mostRecentInvoiceEntrySum =
        typeof mostRecentInvoiceEntry === 'number'
            ? mostRecentInvoiceEntry
            : mostRecentInvoiceEntry?.reduce((acc, item) => acc + item.amount, 0) ?? undefined;

    return hasValue(mostRecentInvoiceEntry) && previewInvoiceSum !== mostRecentInvoiceEntrySum;
};

export const hasError = (invoiceData: any) => {
    return Boolean(invoiceData?.error);
};

export const hasNoItemsToInvoiceErrorCode = (invoiceData: any) => {
    return (
        invoiceData?.errorCode === Constants.NO_ITEMS_TO_INVOICE_ERROR_CODE ||
        invoiceData?.errorCode === Constants.NO_ITEMS_TO_INVOICE_GQL_ERROR_CODE
    );
};

const getNextDateFormattedValue = (date: Moment) => date.format('YYYY-MM-DD');

export const changeInvoiceDateAccordingToSelectedMonth = (
    date: string,
    invoiceDate: Moment,
    selectedPeriodType:
        | typeof DatePeriodPickerConstants.FORM_PERIOD_TYPE_CUSTOM
        | typeof DatePeriodPickerConstants.FORM_PERIOD_TYPE_WEEK
        | typeof DatePeriodPickerConstants.FORM_PERIOD_TYPE_MONTH,
    change: (field: string, nextValue: string) => void,
    onInvoiceDateChange: (value: Moment) => void,
    isInvoiceDateManuallyModified?: boolean,
) => {
    // If the user has already manually set an invoice date, don't override it
    if (isInvoiceDateManuallyModified) {
        return;
    }

    const invoiceMonthMoment = moment(date);
    const todayMoment = moment().startOf('month');

    if (
        invoiceMonthMoment.isBefore(todayMoment) ||
        selectedPeriodType === DatePeriodPickerConstants.FORM_PERIOD_TYPE_CUSTOM
    ) {
        change('invoiceDate', getNextDateFormattedValue(todayMoment));
    } else {
        change('invoiceDate', getNextDateFormattedValue(invoiceMonthMoment));
    }

    onInvoiceDateChange(invoiceDate);
};
